import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import EntityCardList from '../controls/EntityCardList';
import CurrencyField from '../fields/CurrencyField';
import NavRow from '../fields/NavRow';
import SelectField from '../fields/SelectField';
import { TaskDefinition, TaskStepRenderArgs } from '../QuestionnaireShell';
import { AreasOfLawValues, HourlyExceptionsValues, RateExceptionRow } from '../types';
import { currencyAmountSchema, zodResolver } from '../validation';

const rowSchema = z.object({
  radio1: z.literal('Exception'),
  areaOfLaw: z.string().min(1, 'Area of Law is required'),
  amount: currencyAmountSchema,
});

const schema = z.object({
  hourlyRateExceptions: z.array(rowSchema),
});

const DEFAULTS: HourlyExceptionsValues = { hourlyRateExceptions: [] };

const makeEmptyRow = (): RateExceptionRow => ({
  radio1: 'Exception',
  areaOfLaw: '',
  amount: { display: '', value: 0 },
});

const HourlyExceptionsForm = ({
  initialValues,
  allValues,
  onComplete,
  onBack,
  onCancelEdit,
  submitLabel,
}: TaskStepRenderArgs) => {
  const defaults = (initialValues as HourlyExceptionsValues | undefined) ?? DEFAULTS;
  const {
    control,
    handleSubmit,
    trigger,
    formState: { isValid },
  } = useForm<HourlyExceptionsValues>({
    defaultValues: defaults,
    resolver: zodResolver(schema),
    mode: 'onChange',
  });

  const areasOptions = useMemo(() => {
    const selectedAreas = ((allValues.task6 as AreasOfLawValues | undefined)?.areasOfLaw ?? []).flatMap(
      (cat) => cat.choices,
    );
    return selectedAreas.map((title) => ({ title, value: title }));
  }, [allValues.task6]);

  const onSubmit = handleSubmit((values) => onComplete(values));

  return (
    <form className="rnq-form" onSubmit={(e) => e.preventDefault()} style={{ marginTop: '1rem' }}>
      <EntityCardList<HourlyExceptionsValues, RateExceptionRow>
        control={control}
        name="hourlyRateExceptions"
        entityLabel="Exception"
        trigger={trigger}
        makeEmptyRow={makeEmptyRow}
        isRowValid={(row) => rowSchema.safeParse(row).success}
        renderers={{
          getPrimary: (row) => row.amount?.display || '',
          getSecondary: (row) => row.areaOfLaw || null,
          secondaryIcon: 'id_gavel',
        }}
        renderEditForm={(index) => (
          <>
            <SelectField
              control={control}
              name={`hourlyRateExceptions.${index}.areaOfLaw`}
              label="Area of Law"
              required
              options={areasOptions}
            />
            <CurrencyField
              control={control}
              name={`hourlyRateExceptions.${index}.amount`}
              label="Hourly Rate (USD)"
              required
            />
          </>
        )}
      />
      <NavRow
        onBack={onBack}
        onCancelEdit={onCancelEdit}
        onSubmit={onSubmit}
        submitLabel={submitLabel}
        disabled={!isValid}
      />
    </form>
  );
};

const HourlyExceptions: TaskDefinition = {
  key: 'task9',
  label: 'Hourly Exceptions',
  title: 'Are there any areas of law where your hourly rate varies?',
  description: 'If so, please list exceptions below.',
  render: (args) => <HourlyExceptionsForm {...args} />,
};

export default HourlyExceptions;
